// material
import { Button, Grid, CircularProgress, Box, Stack, Skeleton } from '@mui/material'
import TableBody from '@mui/material/TableBody'
import Paper from '@mui/material/Paper'
// redux
import { RootState, useDispatch, useSelector } from '../../redux/store'
import { NotificationEvent as Notification } from '@types'
import NoNotifications from 'pages/NoNotifications'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import { fDateWithTimeZone } from 'utils/formatTime'
import Page from 'components/Page'
import { generateID } from 'utils/generateMessageID'
import { useSocketConnection  } from 'hooks/useSocketConnection';
import { REQUEST_MESSAGE_TYPES, Request } from 'schemas/request'
import { getAllNotificationsSuccess } from 'redux/slices/notifications'
import TaskCounter from './TaskCounter'
import { CenteredContainer, StyledTableCell, StyledTableRow } from './styles/Notifications.styles'
import HeadingTitle from './HeadingTitle'
import { INFO, SECONDARY } from 'theme/palette'

// Skeleton loader for notifications table
const NotificationsSkeleton = () => (
  <Box sx={{ p: 2 }}>
    <Stack spacing={2}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 1,
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ flex: 1, mr: 2 }}>
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
          <Box sx={{ width: 150, mr: 2 }}>
            <Skeleton variant="text" width="100%" height={16} />
          </Box>
          <Box sx={{ width: 100 }}>
            <Skeleton variant="rectangular" width="100%" height={36} />
          </Box>
        </Box>
      ))}
    </Stack>
  </Box>
)

// ----------------------------------------------------------------------

export default function Notifications() {
  const dispatch = useDispatch()

  const { onSocketSend } = useSocketConnection()
  const { notifications, isLoading } = useSelector((state: RootState) => state?.notifications)

  const handleNotification = (id) => {
    const notificationAck: Request = {
      messageId: generateID(),
      messageType: REQUEST_MESSAGE_TYPES.AcknowledgeNotifications,
      AcknowledgeNotifications: { acknowledged: true, businessMessageIds: [id] }
    }
    onSocketSend(notificationAck)
    const filteredNotifications = notifications?.filter((no) => no.businessMessageId !== id)
    dispatch(getAllNotificationsSuccess(filteredNotifications))
  }

  const ackAllNotifications = () => {
    const ids: string[] = []
    notifications.forEach((element: Notification) => {
      element?.businessMessageId ? ids.push(element?.businessMessageId) : undefined
    })
    const notificationsAck: Request = {
      messageId: generateID(),
      messageType: REQUEST_MESSAGE_TYPES.AcknowledgeNotifications,
      AcknowledgeNotifications: { acknowledged: true, businessMessageIds: ids }
    }
    onSocketSend(notificationsAck)
    dispatch(getAllNotificationsSuccess([]))
  }

  return (
    <>
      <Page title="Notifications" sx={{ margin: '0 24px 0 18px', paddingTop: '8px' }}>
        <Grid item sx={{ marginBottom: '18px' }}>
          <HeadingTitle>Notifications</HeadingTitle>
        </Grid>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: '18px', height: '100%' }}
        >
          <TaskCounter type="notification" rows={notifications} />
          {!isLoading && notifications?.length > 0 && (
            <Button
              variant="outlined"
              size="large"
              onClick={() => ackAllNotifications()}
              key="dismiss-all-btn"
              data-testid="dismiss-all-btn"
              sx={{
                backgroundColor: SECONDARY.buttons,
                color: 'white',
                fontSize: '14px',
                fontWeight: 700,
                border: 'none',
                padding: '10px 14px',
                '&:hover': {
                  backgroundColor: INFO.dark,
                  border: 'none'
                }
              }}
            >
              Dismiss all
            </Button>
          )}
        </Grid>

        {isLoading && (
          <NotificationsSkeleton />
        )}
        {!isLoading && (
          <TableContainer component={Paper} title="Notifications">
            <Table sx={{ minWidth: 700 }} aria-label="customized table">
              <TableBody>
                {notifications?.map((row: Notification) => (
                  <StyledTableRow key={row?.businessMessageId}>
                    <StyledTableCell sx={{ width: '75%', padding: '20px' }}>
                      <p>{row?.notificationDisplay}</p>
                    </StyledTableCell>
                    <StyledTableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                      {row?.happenedAt ? fDateWithTimeZone(row?.happenedAt.toString()) : null}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        variant="outlined"
                        sx={{
                          backgroundColor: SECONDARY.buttons,
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 700,
                          border: 'none',
                          padding: '10px 14px',
                          '&:hover': {
                            backgroundColor: INFO.dark,
                            border: 'none'
                          }
                        }}
                        onClick={() => handleNotification(row.businessMessageId)}
                      >
                        Dismiss
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!isLoading && notifications?.length === 0 && (
          <NoNotifications title="No Notifications" type="notifications" />
        )}
      </Page>
    </>
  )
}
